import { useEffect, type RefObject } from "react";

export interface UseFallbackFocusOptions {
  /**
   * Set false to skip the behavior. Lets a caller apply it only on the code path that needs it
   * without calling the hook conditionally.
   */
  enabled?: boolean;
}

/**
 * Focuses `fallbackRef` when nothing inside `containerRef` has taken focus.
 *
 * For a container whose contents decide their own focus — a slot that renders one of many
 * components, some of which autofocus a control and some of which render nothing focusable at all.
 * Rather than tracking which is which, this asks the DOM after the fact and gives focus somewhere
 * sensible only when the container came up empty, so a keyboard user is never left on a view with
 * no focus.
 *
 * Deliberately narrow. It is **not** a general autofocus helper: it never moves focus that already
 * landed inside the container, and it must not be used to respond to state changes or to derive
 * state. A fallback that is missing or `disabled` is left alone, since focusing a disabled control
 * would leave nothing focused at all.
 *
 * @param containerRef - the region checked for held focus
 * @param fallbackRef - focused only when that region holds none
 * @param deps - re-evaluates when these change, like any effect's dependency list; pass whatever
 *   identifies the current contents, since a container that swaps children in place never remounts
 *
 * @example
 * ```tsx
 * // A wizard page renders an arbitrary field; some autofocus an input, some are display-only.
 * useFallbackFocus(fieldContainerRef, nextButtonRef, [activeFieldId]);
 * ```
 */
export const useFallbackFocus = (
  containerRef: RefObject<HTMLElement | null>,
  fallbackRef: RefObject<HTMLElement | null>,
  deps: readonly unknown[],
  options: UseFallbackFocusOptions = {}
): void => {
  const { enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const fallback = fallbackRef.current;
    if (!fallback || fallback.matches(":disabled")) return;

    const container = containerRef.current;
    if (container?.contains(document.activeElement)) return;

    fallback.focus();
    // `containerRef`/`fallbackRef` are stable ref objects, and the caller's `deps` are what actually
    // identify the current contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
};
